import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const COLORS = {
  chantier: "bg-[#FF4D00] text-white",
  devis: "bg-sky-600 text-white",
  opportunite: "bg-zinc-200 text-zinc-700",
};

export default function ArtisanAgenda() {
  const [events, setEvents] = useState([]);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  useEffect(() => {
    apiClient.get("/artisans/me/organizer").then(({ data }) => {
      const evts = [];
      const push = (list, kind) =>
        (list || []).forEach((m) => {
          if (m.deadline) evts.push({ ...m, kind });
        });
      push(data.en_cours, "chantier");
      push(data.a_valider, "chantier");
      push(data.devis_envoyes, "devis");
      push(data.a_chiffrer, "opportunite");
      setEvents(evts);
    }).catch(() => {});
  }, []);

  const y = month.getFullYear();
  const mo = month.getMonth();
  const daysInMonth = new Date(y, mo + 1, 0).getDate();
  const offset = (new Date(y, mo, 1).getDay() + 6) % 7; // Monday-start
  const todayStr = new Date().toISOString().slice(0, 10);
  const dateStr = (day) => `${y}-${String(mo + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const byDay = {};
  events.forEach((e) => {
    if (e.deadline?.startsWith(`${y}-${String(mo + 1).padStart(2, "0")}`)) {
      byDay[e.deadline] = [...(byDay[e.deadline] || []), e];
    }
  });

  const cells = [...Array(offset).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  return (
    <div className="mb-8 border-2 border-[#0A0A0B] bg-white" data-testid="artisan-agenda">
      <div className="border-b-2 border-[#0A0A0B] px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div className="display text-2xl flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-[#FF4D00]" /> Agenda des échéances
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMonth(new Date(y, mo - 1, 1))}
            className="border-2 border-[#0A0A0B] p-1.5 hover:bg-zinc-100"
            data-testid="agenda-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="display text-lg w-40 text-center" data-testid="agenda-month">{MONTHS[mo]} {y}</div>
          <button
            onClick={() => setMonth(new Date(y, mo + 1, 1))}
            className="border-2 border-[#0A0A0B] p-1.5 hover:bg-zinc-100"
            data-testid="agenda-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b border-zinc-200">
        {DAYS.map((d) => (
          <div key={d} className="mono text-[10px] uppercase tracking-widest text-zinc-500 text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, i) => {
          const ds = day ? dateStr(day) : null;
          const evts = ds ? byDay[ds] || [] : [];
          const isToday = ds === todayStr;
          return (
            <div key={i} className={`min-h-[68px] border border-zinc-100 p-1 ${isToday ? "bg-orange-50" : ""}`}>
              {day && (
                <div className={`text-[11px] mono ${isToday ? "text-[#FF4D00] font-bold" : "text-zinc-400"}`}>{day}</div>
              )}
              {evts.map((e) => (
                <Link
                  key={e.id + e.kind}
                  to={`/missions/${e.id}`}
                  title={`${e.title} — ${e.city}`}
                  className={`block truncate text-[10px] px-1 py-0.5 mt-0.5 ${COLORS[e.kind]}`}
                  data-testid={`agenda-event-${e.id}`}
                >
                  {e.title}
                </Link>
              ))}
            </div>
          );
        })}
      </div>
      <div className="px-6 py-3 border-t border-zinc-200 flex flex-wrap gap-4 mono text-[10px] uppercase tracking-widest text-zinc-600">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#FF4D00] inline-block" /> Chantier engagé</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-sky-600 inline-block" /> Devis envoyé</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-zinc-200 inline-block" /> Mission à chiffrer</span>
      </div>
    </div>
  );
}
